import { Box, IconButton, Stack } from '@mui/material';
import TextField from '@mui/material/TextField';
import { DatePicker } from '@mui/x-date-pickers';
import { AsYouType } from 'libphonenumber-js';
import { DateTime } from 'luxon';
import * as React from 'react';
import { useCreateContactField } from '../../../sic-lib/api/hooks/useContact';
import { FieldValidationType } from '../../../sic-lib/enums/FieldValidationType';
import { GenericReducer, genericReducer } from '../../../sic-lib/reducers/genericReducer';
import {
    buildCreateContactField,
    ContactAddress,
    ContactField,
    ContactFieldValue,
} from '../../../sic-lib/types/ContactField';
import { parseContactFieldError } from '../../../sic-lib/utils/contactFieldHelpers';
import EditButtonGroup from '../../Shared/FormControls/Buttons/EditButtonGroup';
import TextFieldFastAddress from '../../Shared/TextFieldFastAddress/TextFieldFastAddress';
import ContactFieldIcon from './ContactFieldIcon';

interface FormState {
    fieldValue: ContactFieldValue;
    isVerified: boolean;
}

interface Props {
    contactId: number;
    contactField: ContactField;
    onToggleHandler: () => void;
}

export default function ContactFieldCreate({ contactId, contactField, onToggleHandler }: Props) {
    const { mutate: createContactField, error } = useCreateContactField(contactId);

    const [formState, formDispatch] = React.useReducer<GenericReducer<FormState>>(genericReducer, {
        fieldValue: contactField.value,
        isVerified: contactField.isVerified,
    });

    const inputType = React.useMemo(() => {
        switch (contactField.validationType) {
            case FieldValidationType.date:
                return 'date';
            case FieldValidationType.years:
            case FieldValidationType.dollars:
                return 'number';
            default:
                return 'text';
        }
    }, [contactField.validationType]);

    const handleSetFieldValue = (event: React.ChangeEvent<HTMLInputElement>) => {
        switch (contactField.validationType) {
            case FieldValidationType.usMobilePhone:
                if (event.target.value.includes('(') && !event.target.value.includes(')')) {
                    //fix a case where hitting backspace on a (416 number, reformats back to (416)
                    formDispatch({
                        fieldValue: event.target.value.replace('(', ''),
                    });
                } else {
                    formDispatch({
                        fieldValue: new AsYouType('US').input(event.target.value),
                    });
                }
                break;
            default:
                formDispatch({ fieldValue: event.target.value });
                break;
        }
    };

    const handleKeyDown = (event: React.KeyboardEvent<HTMLDivElement>) => {
        if (event.key === 'Enter') handleSave();
    };

    const handleSetDateValue = (date: DateTime | null) => {
        if (date) formDispatch({ fieldValue: date.toISO() || undefined });
    };

    const handleSave = async () => {
        const field = buildCreateContactField({
            fieldName: 'value',
            fieldId: contactField.fieldId,
            fieldType: contactField.fieldType,
            leadId: contactId,
            value: formState.fieldValue,
            isVerified: formState.isVerified,
        });

        createContactField(field, {
            onSuccess: data => {
                onToggleHandler();
            },
        });
    };

    return (
        <Stack direction="row" display="flex">
            <Box>
                <IconButton disabled>
                    <ContactFieldIcon validationType={contactField.validationType} />
                </IconButton>
            </Box>
            <Stack spacing={1} width="100%">
                {contactField.validationType === FieldValidationType.address && (
                    <TextFieldFastAddress
                        addressFields={formState.fieldValue as ContactAddress}
                        addressLabel={contactField.label}
                        onChangeHandler={formDispatch}
                        error={!!error}
                        groupHelperText={parseContactFieldError(error)}
                        size="small"
                        fullWidth
                    />
                )}
                {contactField.validationType === FieldValidationType.date && (
                    <DatePicker
                        slotProps={{
                            textField: {
                                name: contactField.label,
                                label: contactField.label,
                                error: !!error,
                                helperText: parseContactFieldError(error),
                            },
                        }}
                        label={contactField.label}
                        value={DateTime.fromISO(formState.fieldValue as string)}
                        format="yyyy/MM/dd"
                        onChange={handleSetDateValue}
                    />
                )}
                {contactField.validationType !== FieldValidationType.address &&
                    contactField.validationType !== FieldValidationType.date && (
                        <TextField
                            id={`fld_${contactField.fieldId}`}
                            name={contactField.label}
                            label={contactField.label}
                            value={formState.fieldValue}
                            type={inputType}
                            error={!!error}
                            helperText={parseContactFieldError(error)}
                            onChange={handleSetFieldValue}
                            onKeyDown={handleKeyDown}
                            size="small"
                            autoFocus
                            fullWidth
                        />
                    )}
                <EditButtonGroup
                    onSave={handleSave}
                    onCancel={onToggleHandler}
                    allowDelete={false}
                    size="small"
                />
            </Stack>
        </Stack>
    );
}
